import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects'

import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../@paco/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    endEventLoggerWithoutAuthApiCall,
    startEventLoggerApiCall,
} from '../../@paco/entities/NotificationCenter/NotificationCenterService'
import { setToast } from '../../@paco/redux/toasts/toastsReducer'
import { ToastType } from '../../@paco/types/toastTypes'

import { getSettings } from '../../helpers/api/settingsApi'
import {
    getProfileOfCurrentUser,
    patchEmergencyContact,
    patchPersonGender,
    patchUserPhoneAndEmail,
    patchUserTransportationOptions,
    postUserAddress,
} from '../../helpers/api/userApi'
import { setSentrySessionUser } from '../../helpers/sentry'
import { checkPermission } from '../../helpers/tools'
import { translate } from '../../helpers/translations/translator'
import {
    CURRENT_USER_EDIT_FAILURE,
    CURRENT_USER_EDIT_REQUEST,
    CURRENT_USER_EDIT_SUCCESS,
    CURRENT_USER_FAILURE,
    CURRENT_USER_REQUEST,
    CURRENT_USER_SAGA_EDIT_ADDRESS,
    CURRENT_USER_SAGA_EDIT_EMAIL,
    CURRENT_USER_SAGA_EDIT_EMERGENCY_CONTACT,
    CURRENT_USER_SAGA_EDIT_GENDER,
    CURRENT_USER_SAGA_EDIT_PHONE,
    CURRENT_USER_SAGA_EDIT_TRANSPORTATION_OPTIONS,
    CURRENT_USER_SAGA_GET_CURRENT_USER,
    CURRENT_USER_SUCCESS,
} from '../actionTypes'
import { setAppError } from '../app/appSaga'
import { unsetTokens } from '../authentication/authenticationSaga'

export function* getCurrentUser() {
    yield put({ type: CURRENT_USER_REQUEST })

    try {
        const [userData, settingsData] = yield all([
            call(() => getProfileOfCurrentUser()),
            call(() => getSettings()),
        ])

        const user = userData.data
        const { roles } = user
        setSentrySessionUser(user)

        const permissions = roles
            .reduce((total, role) => [...total, ...role.permissions], [])
            .map(permission => ({ name: permission.slug, id: permission.id }))
        const { pathname } = document.location
        // Viewer does not have use-employee-app permission, but still needs to be able to reset
        // password or complete registration
        const forgotPassword = pathname.indexOf(translate('nav.forgotPassword.link')) !== -1
        const completeRegistration = pathname.indexOf(translate('nav.completeRegistration.link')) !== -1
        if (! forgotPassword && ! completeRegistration && ! checkPermission(permissions, 'use-employee-app')) {
            yield setAppError({ error: { code: 403 } })
        }

        yield put({
            type: CURRENT_USER_SUCCESS,
            user: userData.data,
            settings: settingsData.data,
            permissions,
        })
    } catch (error) {
        yield put({ type: CURRENT_USER_FAILURE, error: error.response || { status: 401 } })
        yield unsetTokens()
    }
}

function* completeEditAndRoute(title, history) {
    yield history.push(`/${translate('nav.profile.link')}`)
    const toast = translate('pages.profile.updateItemSuccess', { item: title })
    yield put(setToast({ type: ToastType.pass, text: toast }))
}

function* editUserPhone(action) {
    const { phone, userId, history } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        const response = yield call(() => patchUserPhoneAndEmail(userId, phone, null, responseTracker.data))
        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: response.data })
        yield completeEditAndRoute(translate('pages.profile.phone'), history)
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
        yield endEventLoggerApiCall(responseTracker.data)
    }
}

function* editUserEmail(action) {
    const { email, userId, history } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        const response = yield call(() => patchUserPhoneAndEmail(userId, null, email, responseTracker.data))
        yield endEventLoggerWithoutAuthApiCall(responseTracker.data)
        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: response.data })
        history.push(`/${translate('nav.profile.link')}`)
        const toast = translate('pages.profile.updateEmailSuccess')
        yield put(setToast({ type: ToastType.pass, text: toast }))
        yield delay(3500)
        yield put(setToast({ type: ToastType.pass, text: translate('pages.profile.logout') }))
        yield delay(1500)
        yield unsetTokens()
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
    }
}

function* editUserAddress(action) {
    const {
        street,
        houseNumber,
        houseNumberAddition,
        postalCode,
        city,
        personId,
        history,
    } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        const response = yield call(
            () => postUserAddress(
                personId,
                street,
                houseNumber,
                houseNumberAddition,
                postalCode,
                city,
                responseTracker.data,
            ),
        )
        const state = yield select()
        const { currentUser } = state.userReducer
        currentUser.person.addresses.push(response.data)
        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        yield completeEditAndRoute(translate('pages.profile.address'), history)
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
        yield endEventLoggerApiCall(responseTracker.data)
    }
}

function* editEmergencyContact(action) {
    const {
        personId,
        emergencyContactId,
        fullName,
        phone,
        relationType,
        history,
    } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        const response = yield call(
            () => patchEmergencyContact(
                personId, emergencyContactId, fullName, phone, relationType, responseTracker.data,
            ),
        )
        const state = yield select()
        const { currentUser } = state.userReducer
        currentUser.person.emergencyContact = response.data

        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        yield completeEditAndRoute(translate('pages.profile.emergencyContact'), history)
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
        yield endEventLoggerApiCall(responseTracker.data)
    }
}

function* editUserGender(action) {
    const { personId, gender, history } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        const response = yield call(() => patchPersonGender(personId, gender, responseTracker.data))
        const state = yield select()
        const { currentUser } = state.userReducer
        currentUser.person.gender = response.data.gender
        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        yield completeEditAndRoute(translate('common.gender'), history)
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
        yield endEventLoggerApiCall(responseTracker.data)
    }
}

function* editUserTransportationOptions(action) {
    const { transportationOptions, userId, history } = action
    yield put({ type: CURRENT_USER_EDIT_REQUEST })

    const responseTracker = yield startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    try {
        yield call(() => patchUserTransportationOptions(userId, transportationOptions, responseTracker.data))
        const state = yield select()
        const { currentUser } = state.userReducer
        currentUser.transportationOptions = transportationOptions
        yield put({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        yield completeEditAndRoute(translate('pages.profile.transportationOption'), history)
    } catch (error) {
        yield put({ type: CURRENT_USER_EDIT_FAILURE, error: error.response })
    } finally {
        yield endEventLoggerApiCall(responseTracker.data)
    }
}


export default function* userWatcher() {
    yield takeLatest(CURRENT_USER_SAGA_GET_CURRENT_USER, getCurrentUser)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_PHONE, editUserPhone)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_EMAIL, editUserEmail)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_ADDRESS, editUserAddress)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_EMERGENCY_CONTACT, editEmergencyContact)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_GENDER, editUserGender)
    yield takeLatest(CURRENT_USER_SAGA_EDIT_TRANSPORTATION_OPTIONS, editUserTransportationOptions)
}
